import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatWalletAddress } from '../../utils/auth';
import { WalletAvatar } from '../common/WalletAvatar';
import { useUser } from '../../hooks/useUser';

export const UserMenu = ({ userProfile, onSignOut }) => {
  const { t } = useTranslation();
  const { winRate, isLoadingWinRate } = useUser();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (isUserMenuOpen && !target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  return (
    <div className="relative user-menu-container">
      <button 
        className="flex items-center space-x-2 rounded-full border border-transparent hover:border-primary-200 hover:bg-primary-50/60 p-1 transition-colors"
        onClick={toggleUserMenu}
        aria-expanded={isUserMenuOpen}
        aria-haspopup="true"
      >
        <WalletAvatar 
          address={userProfile?.address} 
          size={32} 
          className="ring-2 ring-primary-100 shadow-sm"
        />
        <span className="text-sm font-medium text-neutral-700 hidden md:block">
          {userProfile?.name}
        </span>
        <svg 
          className="w-4 h-4 text-neutral-500 hidden md:block" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* User dropdown menu */}
      {isUserMenuOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white/95 rounded-xl shadow-md py-2 z-50 animate-fade-in border border-neutral-200/80 backdrop-blur-sm">
          {/* 小小的装饰元素 - 树叶 */}
          <div className="absolute top-1 right-1 w-10 h-10 opacity-10 pointer-events-none">
            <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <path d="M20,5 C15,15 5,20 5,30 C5,35 10,40 20,40 C30,40 35,35 35,30 C35,20 25,15 20,5 Z" fill="#5aaf8c" />
            </svg>
          </div>
        
          <div className="px-4 py-3 border-b border-neutral-200">
            <p className="text-sm font-medium text-neutral-800">{userProfile?.name}</p>
            <p className="text-xs text-neutral-500 truncate">
              {formatWalletAddress(userProfile?.address)}
            </p>
            <div className="flex items-center gap-2 mt-1">
              {userProfile?.walletType && (
                <div className="inline-block px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full shadow-sm">
                  {userProfile.walletType === 'ethereum' ? 'Ethereum' : 'Solana'}
                </div>
              )}
              
              {isLoadingWinRate ? (
                <div className="inline-block px-2 py-0.5 bg-neutral-100 text-neutral-500 text-xs rounded-full animate-pulse shadow-sm">
                  {t('nav.winRateLoading')}
                </div>
              ) : winRate ? (
                <div className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full shadow-sm">
                  {t('nav.winRate')}: {winRate}
                </div>
              ) : null}
            </div>
          </div>
          
          <div className="py-1">
            <Link
              to="/user_agents"
              className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-colors mx-1 rounded-lg"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mr-2"></span>
                {t('nav.myAgents')}
              </div>
            </Link>
            <Link
              to="/game_history"
              className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-colors mx-1 rounded-lg"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mr-2"></span>
                {t('nav.gameHistory')}
              </div>
            </Link>
          </div>
          
          <div className="border-t border-neutral-200 mt-2">
            <button
              onClick={() => {
                onSignOut();
                setIsUserMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm text-accent-700 hover:bg-accent-50 transition-colors mx-1 mt-1 rounded-lg"
            >
              <div className="flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 mr-2"></span>
                {t('auth.disconnectWallet')}
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
